import type { HsMeasurementPoint } from './measurement.function';

/** What a click snapped to: a mesh corner, the middle of an edge, a point on an edge, or the face itself. */
export type HsSnapKind = 'vertex' | 'midpoint' | 'edge' | 'face';

/** A snapped point with what it snapped to and the normal of the face under the click, when one was found. */
export interface HsSnapResult {
  point: HsMeasurementPoint;
  kind: HsSnapKind;
  normal: HsMeasurementPoint | null;
}

/** The triangles of a mesh in its own coordinates: xyz per vertex, three indices per triangle (or none). */
export interface HsSnapMesh {
  readonly vertices: ArrayLike<number>;
  readonly indices?: ArrayLike<number> | null;
}

/** Triangles sharing an edge at less than about 10 degrees form one flat face; that edge does not snap. */
const FLAT_EDGE_COSINE: number = Math.cos((10 * Math.PI) / 180);

/** Edges snap a little closer than corners, so a click near a corner prefers the corner. */
const EDGE_TOLERANCE_SHARE: number = 0.6;

/**
 * Snaps a surface hit to the nearest corner, edge middle or edge of the mesh within `tolerance` world meters, as CAD
 * viewers do for measurements; otherwise keeps the hit as a face point. `matrix` (column-major) places the mesh in
 * the world. The normal is that of the triangle nearest to the hit; a hit farther than `tolerance` from every
 * triangle (a mesh that does not match the drawn object) keeps the hit and has no normal.
 */
export function hsSnapToMesh(
  mesh: HsSnapMesh,
  matrix: ArrayLike<number>,
  hit: HsMeasurementPoint,
  tolerance: number
): HsSnapResult {
  const world: Float64Array = transformVertices(mesh.vertices, matrix);
  const indices: ArrayLike<number> | null =
    mesh.indices !== undefined && mesh.indices !== null && mesh.indices.length > 0 ? mesh.indices : null;
  const triangleCount: number = indices === null ? Math.floor(world.length / 9) : Math.floor(indices.length / 3);
  let vertex: { point: number[]; distance: number } | null = null;
  let midpoint: { point: number[]; distance: number } | null = null;
  let edge: { point: number[]; distance: number } | null = null;
  let face: { normal: number[]; distance: number } | null = null;
  // Edges of the triangles near the click, with the normals of the triangles sharing them.
  const edges: Map<string, { start: number[]; end: number[]; normals: number[][] }> = new Map();
  const corners: number[][] = [[], [], []];
  for (let triangle: number = 0; triangle < triangleCount; triangle++) {
    for (let corner: number = 0; corner < 3; corner++) {
      const index: number = indices === null ? triangle * 3 + corner : indices[triangle * 3 + corner];
      corners[corner] = [world[index * 3], world[index * 3 + 1], world[index * 3 + 2]];
    }
    const distance: number = distanceToTriangle(hit, corners[0], corners[1], corners[2]);
    if (distance > tolerance) {
      continue;
    }
    const triangleNormal: number[] = normalize(
      cross(subtract(corners[1], corners[0]), subtract(corners[2], corners[0]))
    );
    if (length(triangleNormal) > 0 && (face === null || distance < face.distance)) {
      face = { normal: triangleNormal, distance };
    }
    for (let corner: number = 0; corner < 3; corner++) {
      const start: number[] = corners[corner];
      const end: number[] = corners[(corner + 1) % 3];
      vertex = nearer(vertex, start, hit);
      const key: string = edgeKey(start, end);
      const known: { start: number[]; end: number[]; normals: number[][] } | undefined = edges.get(key);
      if (known === undefined) {
        edges.set(key, { start, end, normals: [triangleNormal] });
      } else {
        known.normals.push(triangleNormal);
      }
    }
  }
  for (const { start, end, normals } of edges.values()) {
    // A diagonal between two triangles of one flat face is an artifact of triangulation, not an edge of the model.
    if (normals.length > 1 && normals.every((other: number[]) => dot(other, normals[0]) > FLAT_EDGE_COSINE)) {
      continue;
    }
    midpoint = nearer(midpoint, [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2, (start[2] + end[2]) / 2], hit);
    edge = nearer(edge, closestOnSegment(hit, start, end), hit);
  }
  const normal: HsMeasurementPoint | null = face === null ? null : [face.normal[0], face.normal[1], face.normal[2]];
  if (vertex !== null && vertex.distance <= tolerance) {
    return { point: toPoint(vertex.point), kind: 'vertex', normal };
  }
  if (midpoint !== null && midpoint.distance <= tolerance) {
    return { point: toPoint(midpoint.point), kind: 'midpoint', normal };
  }
  if (edge !== null && edge.distance <= tolerance * EDGE_TOLERANCE_SHARE) {
    return { point: toPoint(edge.point), kind: 'edge', normal };
  }
  return { point: [hit[0], hit[1], hit[2]], kind: 'face', normal };
}

/** World meters that `pixels` cover at `distance` in front of a perspective camera with vertical `fov` (radians). */
export function hsPixelsToWorld(pixels: number, distance: number, fov: number, viewportHeight: number): number {
  return viewportHeight <= 0 ? 0 : (pixels * 2 * distance * Math.tan(fov / 2)) / viewportHeight;
}

function transformVertices(vertices: ArrayLike<number>, matrix: ArrayLike<number>): Float64Array {
  const result: Float64Array = new Float64Array(vertices.length - (vertices.length % 3));
  for (let index: number = 0; index < result.length; index += 3) {
    const x: number = vertices[index];
    const y: number = vertices[index + 1];
    const z: number = vertices[index + 2];
    result[index] = matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12];
    result[index + 1] = matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13];
    result[index + 2] = matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14];
  }
  return result;
}

/** The same key for an edge in either direction; positions are rounded to a tenth of a millimeter. */
function edgeKey(a: number[], b: number[]): string {
  const first: string = a.map((value: number) => Math.round(value * 10000)).join(',');
  const second: string = b.map((value: number) => Math.round(value * 10000)).join(',');
  return first < second ? `${first}|${second}` : `${second}|${first}`;
}

function nearer(
  best: { point: number[]; distance: number } | null,
  point: number[],
  hit: ArrayLike<number>
): { point: number[]; distance: number } {
  const distance: number = Math.hypot(point[0] - hit[0], point[1] - hit[1], point[2] - hit[2]);
  return best === null || distance < best.distance ? { point, distance } : best;
}

function closestOnSegment(point: ArrayLike<number>, start: number[], end: number[]): number[] {
  const direction: number[] = subtract(end, start);
  const lengthSquared: number = dot(direction, direction);
  const t: number =
    lengthSquared === 0
      ? 0
      : Math.max(0, Math.min(1, dot(subtract([point[0], point[1], point[2]], start), direction) / lengthSquared));
  return [start[0] + direction[0] * t, start[1] + direction[1] * t, start[2] + direction[2] * t];
}

/** Distance from a point to a triangle (Ericson, Real-Time Collision Detection, 5.1.5). */
function distanceToTriangle(point: ArrayLike<number>, a: number[], b: number[], c: number[]): number {
  const p: number[] = [point[0], point[1], point[2]];
  const ab: number[] = subtract(b, a);
  const ac: number[] = subtract(c, a);
  const ap: number[] = subtract(p, a);
  const d1: number = dot(ab, ap);
  const d2: number = dot(ac, ap);
  if (d1 <= 0 && d2 <= 0) {
    return pointDistance(p, a);
  }
  const bp: number[] = subtract(p, b);
  const d3: number = dot(ab, bp);
  const d4: number = dot(ac, bp);
  if (d3 >= 0 && d4 <= d3) {
    return pointDistance(p, b);
  }
  const vc: number = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    return pointDistance(p, add(a, scale(ab, d1 / (d1 - d3))));
  }
  const cp: number[] = subtract(p, c);
  const d5: number = dot(ab, cp);
  const d6: number = dot(ac, cp);
  if (d6 >= 0 && d5 <= d6) {
    return pointDistance(p, c);
  }
  const vb: number = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    return pointDistance(p, add(a, scale(ac, d2 / (d2 - d6))));
  }
  const va: number = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    return pointDistance(p, add(b, scale(subtract(c, b), (d4 - d3) / (d4 - d3 + (d5 - d6)))));
  }
  const denominator: number = 1 / (va + vb + vc);
  return pointDistance(p, add(a, add(scale(ab, vb * denominator), scale(ac, vc * denominator))));
}

function toPoint(point: number[]): HsMeasurementPoint {
  return [point[0], point[1], point[2]];
}

function add(a: number[], b: number[]): number[] {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function subtract(a: number[], b: number[]): number[] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function scale(a: number[], factor: number): number[] {
  return [a[0] * factor, a[1] * factor, a[2] * factor];
}

function dot(a: number[], b: number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: number[], b: number[]): number[] {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function length(a: number[]): number {
  return Math.hypot(a[0], a[1], a[2]);
}

function normalize(a: number[]): number[] {
  const size: number = length(a);
  return size === 0 ? [0, 0, 0] : [a[0] / size, a[1] / size, a[2] / size];
}

function pointDistance(a: number[], b: number[]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}
